import React, { useState, useEffect } from 'react';
import { withRouter } from '../../common/withRouter';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import { Notification, ButtonOBSOLETED, Loader, Message } from '../../web.common';
import withApi from '../higherOrderComponents/withApi';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';
import { LANGUAGE_OPTIONS } from './seoOptions';

/**
 * One facility's copy in one language. Same form as SEO Texts, with the facility in place of the
 * area/sport pair.
 *
 * An empty field is not an empty page: it means the site keeps generating that part. Clearing
 * every field removes the row altogether, which is how an editor goes back to the generated texts.
 */

const TITLE_MAX = 60;
const META_MIN = 120;
const META_MAX = 155;

const FacilityTextAddInternal = ({ api, history, match, location }) => {
  const id = match.params.id;
  const isEditMode = !!id;
  // Arriving from a facility screen: the facility is already decided, so it is not searched again.
  const presetFacilityId = new URLSearchParams(location.search).get('facilityId');

  const initialValue = {
    Facility: null,
    Language: '1', // Danish
    MetaTitle: '',
    MetaDescription: '',
    H1Title: '',
    Description: ''
  };

  const [formData, setFormData] = useState(initialValue);
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(isEditMode || !!presetFacilityId);

  useEffect(() => {
    if (isEditMode) {
      loadData();
    } else if (presetFacilityId) {
      loadPresetFacility();
    }
  }, [id]);

  const loadPresetFacility = async () => {
    try {
      const facility = await api.facilities.getFacility(presetFacilityId);
      setFormData((previous) => ({
        ...previous,
        Facility: { value: presetFacilityId, label: facility?.Name || presetFacilityId }
      }));
    } catch (e) {
      setNotification(new Message('error', 'Unable to load the facility; pick it by name instead.'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const row = await api.facilityContent.getFacilityText(id);
      setFormData({
        Facility: { value: row.facilityId, label: row.facilityName || row.facilityId },
        Language: String(row.language),
        MetaTitle: row.metaTitle || '',
        MetaDescription: row.metaDescription || '',
        H1Title: row.h1Title || '',
        Description: row.description || ''
      });
      setIsLoading(false);
    } catch (e) {
      setNotification(new Message('error', 'Unable to load the facility text'));
      setIsLoading(false);
    }
  };

  const formLabelStyle = { display: "table-cell", width: "20%", whiteSpace: "nowrap", fontWeight: "bold", verticalAlign: "top" };
  const formSpanStyle = { display: "table-cell" };
  const rowStyle = { display: "table", width: "100%", margin: "auto", marginBottom: "0" };
  const containerStyle = { width: '80%', margin: 'auto', backgroundColor: 'white', border: "1px solid #e5e5e5", padding: "1.2rem" };
  const helpTextStyle = { fontSize: '0.85rem', color: '#666', fontStyle: 'italic', marginTop: '5px' };
  const counterStyle = (bad) => ({ fontSize: '0.85rem', color: bad ? '#b00' : '#666', marginTop: '5px' });

  const titleLength = formData.MetaTitle.trim().length;
  const metaLength = formData.MetaDescription.trim().length;
  const titleTooLong = titleLength > TITLE_MAX;
  const metaOutOfRange = metaLength > 0 && (metaLength < META_MIN || metaLength > META_MAX);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.Facility || !formData.Facility.value) {
      setNotification(new Message('error', 'A facility is required.'));
      return;
    }
    if (titleTooLong) {
      setNotification(new Message('error', `The SEO title is ${titleLength} characters; the maximum is ${TITLE_MAX}.`));
      return;
    }
    if (metaOutOfRange) {
      setNotification(new Message('error', `The meta description is ${metaLength} characters; it must be between ${META_MIN} and ${META_MAX}.`));
      return;
    }

    const payload = {
      FacilityId: formData.Facility.value,
      Language: parseInt(formData.Language, 10),
      MetaTitle: formData.MetaTitle.trim() || null,
      MetaDescription: formData.MetaDescription.trim() || null,
      H1Title: formData.H1Title.trim() || null,
      Description: formData.Description.trim() || null
    };

    try {
      const result = await api.facilityContent.saveFacilityText(payload);
      if (result && result.success === false) {
        setNotification(new Message('error', result.message || 'Unable to save'));
        return;
      }

      if (result && result.content == null) {
        setNotification(new Message('success', 'Every field was empty, so the entry was removed. The site goes back to the generated texts within a couple of minutes.'));
        setTimeout(() => history.push('/facility-texts'), 1500);
        return;
      }

      setNotification(new Message('success', 'Saved. The site picks it up within a couple of minutes.'));

      if (!isEditMode && result && result.content && result.content.id) {
        setTimeout(() => history.push(`/facility-text/${result.content.id}`), 1500);
      }
    } catch (e) {
      let errorMessage = 'Unable to save the facility text';
      if (e.payload?.message) errorMessage = e.payload.message;
      else if (e.message && e.message !== 'Server returned error: ') errorMessage = e.message.replace('Server returned error: ', '');
      setNotification(new Message('error', errorMessage));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete these texts? The page goes back to the generated ones.')) {
      return;
    }
    try {
      await api.facilityContent.deleteFacilityText({
        facilityId: formData.Facility.value,
        contentLanguage: parseInt(formData.Language, 10)
      });
      setNotification(new Message('success', 'Deleted.'));
      setTimeout(() => history.push('/facility-texts'), 1500);
    } catch (e) {
      setNotification(new Message('error', 'Unable to delete the facility text'));
    }
  };

  if (isLoading) {
    return <Loader hasLoaded={false}>Loading...</Loader>;
  }

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={notification} />
          <PageTop
            documentTitle={isEditMode ? 'Edit Facility Text' : 'New Facility Text'}
            documentDescription={isEditMode ? 'Edit Facility Text' : 'New Facility Text'}
          />

          <h2 className='main-page-header main-page-header-with-subheader'>
            {isEditMode ? 'Edit Facility Text' : 'New Facility Text'}
          </h2>
          <h5 className='main-page-subheader'>
            <i>{isEditMode ? `Editing ${formData.Facility ? formData.Facility.label : ''}` : 'Create a new per-facility/language entry'}</i>
          </h5>
          <br />
          <div style={containerStyle}>
            <form onSubmit={handleSubmit}>
              <div style={rowStyle}>
                <label htmlFor="facility" style={formLabelStyle}>{'Facility*'}</label>
                <span style={formSpanStyle}>
                  {isEditMode || presetFacilityId ? (
                    <span>{formData.Facility ? formData.Facility.label : ''}</span>
                  ) : (
                    <AsyncSelect
                      className="react-select-filters-section"
                      classNamePrefix="react-select-filters-section-prefix"
                      defaultOptions
                      cacheOptions
                      value={formData.Facility}
                      onChange={(selection) => setFormData({ ...formData, Facility: selection })}
                      loadOptions={(inputValue) => api.facilities.lookupByNameOrId(inputValue)}
                    />
                  )}
                  <div style={helpTextStyle}>
                    {isEditMode
                      ? 'The facility and the language identify the entry; to move the texts elsewhere, create a new one.'
                      : (presetFacilityId ? 'Coming from the facility screen.' : 'Search by facility name.')}
                  </div>
                </span>
              </div>
              <hr />

              <div style={rowStyle}>
                <label htmlFor="language" style={formLabelStyle}>{'Language*'}</label>
                <span style={formSpanStyle}>
                  <select
                    name="language"
                    id="language"
                    value={formData.Language}
                    onChange={({ target }) => setFormData({ ...formData, Language: target.value })}
                    style={{ width: '100%', padding: '5px' }}
                    disabled={isEditMode}
                  >
                    {LANGUAGE_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                  </select>
                  <div style={helpTextStyle}>Texts are never shown under another language: what is empty here stays generated on that language&apos;s page.</div>
                </span>
              </div>
              <hr />

              <div style={rowStyle}>
                <label htmlFor="metaTitle" style={formLabelStyle}>{'SEO Title'}</label>
                <span style={formSpanStyle}>
                  <input
                    name="metaTitle"
                    id="metaTitle"
                    value={formData.MetaTitle}
                    onChange={({ target }) => setFormData({ ...formData, MetaTitle: target.value })}
                    style={{ width: '100%' }}
                  />
                  <div style={counterStyle(titleTooLong)}>{titleLength}/{TITLE_MAX}</div>
                  <div style={helpTextStyle}>The &lt;title&gt; in the search result. Leave empty to keep the generated one.</div>
                </span>
              </div>
              <hr />

              <div style={rowStyle}>
                <label htmlFor="metaDescription" style={formLabelStyle}>{'Meta Description'}</label>
                <span style={formSpanStyle}>
                  <textarea
                    name="metaDescription"
                    id="metaDescription"
                    value={formData.MetaDescription}
                    onChange={({ target }) => setFormData({ ...formData, MetaDescription: target.value })}
                    style={{ width: '100%', minHeight: '60px' }}
                  />
                  <div style={counterStyle(metaOutOfRange)}>{metaLength} characters ({META_MIN}&ndash;{META_MAX})</div>
                  <div style={helpTextStyle}>The grey text under the title in Google. Leave empty to keep the generated one.</div>
                </span>
              </div>
              <hr />

              <div style={rowStyle}>
                <label htmlFor="h1Title" style={formLabelStyle}>{'H1 Heading'}</label>
                <span style={formSpanStyle}>
                  <input
                    name="h1Title"
                    id="h1Title"
                    value={formData.H1Title}
                    onChange={({ target }) => setFormData({ ...formData, H1Title: target.value })}
                    style={{ width: '100%' }}
                    maxLength={200}
                  />
                  <div style={helpTextStyle}>
                    The heading at the top of the page; not the title tag, and no SEO length limit.
                    Keep the facility name in it &mdash; the name is what people search for, and it is
                    the strongest signal the page has. Add to it rather than replace it:
                    &quot;UNI10 Padel Kolding &ndash; padel i Kolding&quot;, not &quot;Padel i Kolding&quot;.
                    Leave empty to keep the facility name.
                  </div>
                </span>
              </div>
              <hr />

              <div style={rowStyle}>
                <label htmlFor="description" style={formLabelStyle}>{'Facility Text (Markdown)'}</label>
                <span style={formSpanStyle}>
                  <textarea
                    name="description"
                    id="description"
                    value={formData.Description}
                    onChange={({ target }) => setFormData({ ...formData, Description: target.value })}
                    style={{ width: '100%', minHeight: '160px' }}
                  />
                  <div style={helpTextStyle}>Shown in the block near the bottom of the facility page. Leave empty for no text at all.</div>
                </span>
              </div>
              <hr />

              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <ButtonOBSOLETED
                  buttonColor="primary"
                  type="submit"
                  buttonContent={isEditMode ? "Update" : "Create"}
                  ariaLabel={isEditMode ? "Update facility text" : "Create facility text"}
                  disabled={false}
                />
                {isEditMode && (
                  <span style={{ marginLeft: '15px' }}>
                    <ButtonOBSOLETED
                      buttonColor="gray"
                      type="button"
                      onClick={handleDelete}
                      buttonContent="Delete"
                      ariaLabel="Delete facility text"
                      disabled={false}
                    />
                  </span>
                )}
                <span style={{ marginLeft: '15px' }}>
                  <ButtonOBSOLETED
                    buttonColor="gray"
                    type="button"
                    onClick={() => history.push('/facility-texts')}
                    buttonContent="Cancel"
                    ariaLabel="Cancel"
                    disabled={false}
                  />
                </span>
              </div>
            </form>
          </div>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

FacilityTextAddInternal.propTypes = {
  api: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
  location: PropTypes.object,
  isLoggedIn: PropTypes.bool
};

const FacilityTextAdd = withRouter(
  withApi(
    connect(({ user }) => ({ isLoggedIn: !!user }), {})(FacilityTextAddInternal)
  )
);

FacilityTextAdd.requiresLogin = true;

export default FacilityTextAdd;
